import { useState, useEffect } from "react";
import axios from "axios";
import {
  BookOpen, Sparkles, ChevronDown, ChevronUp, Save, Loader2,
  Wand2, Check, RotateCcw, Edit3, FileText
} from "lucide-react";
import { API } from "../lib/constants";

export default function StoryInput({ seriesOverride }) {
  const series = seriesOverride || "the-notarys-shadow";
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outline, setOutline] = useState("");
  const [expanding, setExpanding] = useState(false);
  const [expandResult, setExpandResult] = useState(null);
  const [editingEp, setEditingEp] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingEp, setSavingEp] = useState(null);
  const [expandingSingle, setExpandingSingle] = useState(null);
  const [seasonProgress, setSeasonProgress] = useState([]);
  const [autoExpanding, setAutoExpanding] = useState(false);
  const [autoProgress, setAutoProgress] = useState({ done: 0, total: 0 });
  const [expandJobId, setExpandJobId] = useState(null);
  const [expandDone, setExpandDone] = useState(false);

  // Load season progress
  useEffect(() => {
    axios.get(`${API}/episodes/season-progress?series=${series}`)
      .then(res => {
        setSeasonProgress(res.data);
        const allSeasons = res.data.map(s => s.season).sort((a, b) => a - b);
        setSeasons(allSeasons);
        if (allSeasons.length > 0 && !selectedSeason) setSelectedSeason(allSeasons[0]);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [series]);

  // Load episodes when season changes
  useEffect(() => {
    if (selectedSeason === null) return;
    setLoading(true);
    axios.get(`${API}/episodes?series=${series}&season=${selectedSeason}&limit=100`)
      .then(res => {
        const eps = Array.isArray(res.data) ? res.data : res.data.episodes || [];
        setEpisodes(eps.sort((a, b) => a.episode_number - b.episode_number));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedSeason, series]);

  const expandSeason = async () => {
    if (!outline.trim()) return;
    setExpanding(true);
    setExpandResult(null);
    try {
      const res = await axios.post(`${API}/episodes/expand`, {
        series, season: selectedSeason, outline: outline.trim()
      }, { timeout: 90000 });
      setExpandResult(res.data);
      // Refresh episodes
      const epRes = await axios.get(`${API}/episodes?series=${series}&season=${selectedSeason}&limit=100`);
      const eps = Array.isArray(epRes.data) ? epRes.data : epRes.data.episodes || [];
      setEpisodes(eps.sort((a, b) => a.episode_number - b.episode_number));
    } catch (e) {
      console.error(e);
      alert("Expansion failed: " + (e.response?.data?.detail || e.message));
    } finally { setExpanding(false); }
  };

  const autoExpand = async () => {
    setAutoExpanding(true);
    setExpandResult(null);
    setExpandDone(false);
    setAutoProgress({ done: 0, total: episodes.length });

    try {
      // Start background job
      const res = await axios.post(`${API}/episodes/auto-expand`, {
        series, season: selectedSeason
      }, { timeout: 30000 });
      const jobId = res.data.job_id;
      setExpandJobId(jobId);

      // Poll for progress
      const poll = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/episodes/expand-status/${jobId}`);
          const job = statusRes.data;
          setAutoProgress({ done: job.progress || 0, total: job.total || episodes.length });

          if (job.status === "done") {
            clearInterval(poll);
            setAutoExpanding(false);
            setExpandDone(true);
            setExpandResult({ expanded: job.expanded, total: job.total });
            // Refresh episodes
            const epRes = await axios.get(`${API}/episodes?series=${series}&season=${selectedSeason}&limit=100`);
            const eps = Array.isArray(epRes.data) ? epRes.data : epRes.data.episodes || [];
            setEpisodes(eps.sort((a, b) => a.episode_number - b.episode_number));
          } else if (job.status === "error") {
            clearInterval(poll);
            setAutoExpanding(false);
            alert("Expansion failed: " + (job.error || "Unknown error"));
          }
        } catch (pollErr) {
          console.error("Poll error:", pollErr);
        }
      }, 3000);

    } catch (e) {
      console.error(e);
      setAutoExpanding(false);
      alert("Failed to start expansion: " + (e.response?.data?.detail || e.message));
    }
  };

  const expandSingle = async (ep) => {
    setExpandingSingle(ep.id);
    try {
      const res = await axios.post(`${API}/episodes/expand-single`, {
        episode_id: ep.id, outline, series
      }, { timeout: 90000 });
      setEpisodes(prev => prev.map(e => e.id === ep.id ? { ...e, summary: res.data.summary } : e));
    } catch (e) {
      alert("Failed: " + (e.response?.data?.detail || e.message));
    } finally { setExpandingSingle(null); }
  };

  const startEdit = (ep) => {
    setEditingEp(ep.id);
    setEditText(ep.summary || ep.full_story || "");
  };

  const saveEdit = async (ep) => {
    setSavingEp(ep.id);
    try {
      await axios.patch(`${API}/episodes/${ep.id}/content`, { summary: editText });
      setEpisodes(prev => prev.map(e => e.id === ep.id ? { ...e, summary: editText } : e));
      setEditingEp(null);
    } catch (e) {
      alert("Save failed");
    } finally { setSavingEp(null); }
  };

  const getProgress = (seasonNum) => {
    const p = seasonProgress.find(s => s.season === seasonNum);
    if (!p) return { total: 0, expanded: 0 };
    const expanded = episodes.filter(e => (e.summary && e.summary.length > 100) || (e.full_story && e.full_story.length > 100)).length;
    return { ...p, expanded };
  };

  const summaryQuality = (ep) => {
    const text = ep?.summary || ep?.full_story;
    if (!text) return "empty";
    if (text.length < 80) return "short";
    if (text.length < 200) return "basic";
    return "rich";
  };

  const getDisplayText = (ep) => {
    return ep.summary || (ep.full_story ? ep.full_story.substring(0, 300) + "..." : "No summary yet");
  };

  const qualityColor = {
    empty: "bg-gray-700",
    short: "bg-amber-500/60",
    basic: "bg-blue-500/60",
    rich: "bg-green-500/60",
  };

  const qualityLabel = {
    empty: "No summary",
    short: "Needs expansion",
    basic: "Basic",
    rich: "Rich",
  };

  return (
    <div data-testid="story-input">
      {/* Season Selector */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-cinzel text-xl font-bold text-white mb-1">Story Input</h2>
          <p className="text-gray-500 text-sm">Provide season outlines and let AI expand your episode summaries</p>
        </div>
      </div>

      {/* Season Pills */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="season-selector">
        {seasons.map(s => {
          const prog = seasonProgress.find(p => p.season === s);
          return (
            <button key={s} onClick={() => { setSelectedSeason(s); setExpandResult(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all relative ${
                selectedSeason === s
                  ? "bg-red-600 text-white"
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              }`}
              data-testid={`season-btn-${s}`}>
              S{s}
              <span className="ml-1.5 text-[10px] opacity-60">{prog?.total || 0} eps</span>
            </button>
          );
        })}
      </div>

      {selectedSeason && (
        <>
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mb-6" data-testid="expand-actions">
            <button
              onClick={autoExpand}
              disabled={autoExpanding || expanding}
              className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:opacity-50 text-white text-sm font-bold rounded-lg transition-colors"
              data-testid="auto-expand-btn"
            >
              {autoExpanding ? (
                <><Loader2 size={14} className="animate-spin" /> Running in background...</>
              ) : (
                <><Wand2 size={14} /> Auto Expand Season</>
              )}
            </button>
            {!autoExpanding && <span className="text-gray-600 text-xs self-center">Runs in background — you can navigate away freely</span>}
          </div>

          {/* Optional Outline */}
          <details className="mb-6 rounded-xl bg-white/[0.02] border border-white/10" data-testid="outline-section">
            <summary className="flex items-center gap-2 p-4 cursor-pointer text-gray-400 hover:text-white transition-colors">
              <BookOpen size={16} className="text-red-500" />
              <span className="font-cinzel font-bold text-sm">Custom Outline (Optional)</span>
              <span className="text-gray-600 text-xs ml-2">Provide extra context to guide AI expansion</span>
            </summary>
            <div className="px-5 pb-5">
              <textarea
                value={outline}
                onChange={e => setOutline(e.target.value)}
                rows={4}
                placeholder={`Describe Season ${selectedSeason}'s story arc...\n\nExample: "Vanessa discovers the notary fraud scheme extends into her own family. Teka uncovers a secret ledger. The Strategist makes his first move..."`}
                className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-red-500/50 outline-none resize-y"
                data-testid="outline-input"
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-gray-600 text-[10px]">{outline.length} chars</span>
                <button
                  onClick={expandSeason}
                  disabled={expanding || autoExpanding || !outline.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 disabled:opacity-30 text-white text-sm font-bold rounded-lg transition-colors"
                  data-testid="expand-season-btn"
                >
                  {expanding ? (
                    <><Loader2 size={14} className="animate-spin" /> Expanding...</>
                  ) : (
                    <><Sparkles size={14} /> Expand with Outline</>
                  )}
                </button>
              </div>
            </div>
          </details>

          {/* Expansion Result */}
          {expandDone && expandResult && (
            <div className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="flex items-center gap-2">
                <Check size={16} className="text-green-400" />
                <span className="text-green-400 text-sm font-bold">
                  Done! Expanded {expandResult.expanded} of {expandResult.total} episodes
                </span>
                <span className="text-gray-500 text-xs ml-auto">You can navigate away — expansion runs in the background</span>
              </div>
            </div>
          )}

          {/* Active job indicator (visible even after re-visiting page) */}
          {autoExpanding && (
            <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-center gap-3">
                <Loader2 size={16} className="text-amber-400 animate-spin" />
                <div className="flex-1">
                  <span className="text-amber-400 text-sm font-bold">
                    Expanding Season {selectedSeason}... {autoProgress.done}/{autoProgress.total}
                  </span>
                  <p className="text-gray-500 text-xs mt-0.5">Running in background — you can navigate to other pages and come back</p>
                </div>
                <div className="flex items-center gap-2 w-32">
                  <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full transition-all duration-500"
                      style={{ width: `${autoProgress.total > 0 ? (autoProgress.done / autoProgress.total) * 100 : 0}%` }} />
                  </div>
                  <span className="text-amber-400 text-[10px] font-mono">{autoProgress.total > 0 ? Math.round((autoProgress.done / autoProgress.total) * 100) : 0}%</span>
                </div>
              </div>
            </div>
          )}

          {/* Episode List */}
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
            </div>
          ) : (
            <div className="space-y-2" data-testid="episode-list">
              {episodes.map(ep => {
                const quality = summaryQuality(ep);
                const isEditing = editingEp === ep.id;
                const isSaving = savingEp === ep.id;
                const isExpandingSingle = expandingSingle === ep.id;

                return (
                  <div key={ep.id}
                    className="p-4 rounded-lg bg-white/[0.02] border border-white/8 hover:border-white/15 transition-all"
                    data-testid={`episode-row-${ep.id}`}>
                    <div className="flex items-start gap-3">
                      {/* Episode number + quality dot */}
                      <div className="flex flex-col items-center gap-1 pt-0.5">
                        <span className="text-[10px] font-mono text-gray-500 w-8 text-center">
                          E{ep.episode_number}
                        </span>
                        <div className={`w-2 h-2 rounded-full ${qualityColor[quality]}`}
                          title={qualityLabel[quality]} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-cinzel font-bold text-white text-sm mb-1">
                          {ep.title}
                        </h4>

                        {isEditing ? (
                          <div>
                            <textarea
                              value={editText}
                              onChange={e => setEditText(e.target.value)}
                              rows={4}
                              className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/15 rounded text-sm text-white outline-none resize-y focus:border-red-500/50"
                              data-testid={`edit-summary-${ep.id}`}
                            />
                            <div className="flex gap-2 mt-2">
                              <button onClick={() => saveEdit(ep)} disabled={isSaving}
                                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-[10px] font-bold rounded transition-colors"
                                data-testid={`save-edit-${ep.id}`}>
                                {isSaving ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />} Save
                              </button>
                              <button onClick={() => setEditingEp(null)}
                                className="px-3 py-1.5 bg-white/5 text-gray-400 text-[10px] font-bold rounded hover:bg-white/10">
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className={`text-xs leading-relaxed ${quality === "rich" ? "text-gray-300" : "text-gray-500"}`}>
                            {getDisplayText(ep)}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      {!isEditing && (
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => startEdit(ep)}
                            className="p-1.5 text-gray-600 hover:text-white hover:bg-white/10 rounded transition-colors"
                            title="Edit"
                            data-testid={`edit-btn-${ep.id}`}>
                            <Edit3 size={12} />
                          </button>
                          <button onClick={() => expandSingle(ep)} disabled={isExpandingSingle}
                            className="p-1.5 text-gray-600 hover:text-amber-400 hover:bg-amber-500/10 rounded transition-colors"
                            title="AI Expand"
                            data-testid={`expand-single-${ep.id}`}>
                            {isExpandingSingle ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div className="mt-6 flex items-center gap-4 text-[10px] text-gray-600">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-700" /> No summary</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500/60" /> Needs expansion</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500/60" /> Basic</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500/60" /> Rich</span>
          </div>
        </>
      )}
    </div>
  );
}
